import { z } from 'zod';

// Production event names the legacy signup app already emits, kept byte-for-byte
// so existing funnels and dashboards keep reading them after the signup surface
// moves. They break the lowercase naming grammar on purpose, so the list is
// closed: adding a name means amending ADR-166, never appending here.
export const LEGACY_PARITY_EVENT_NAMES = Object.freeze([
  'Growth.RegFlow.Viewed',
  'Growth.RegFlow.EmployerSearched',
  'Growth.RegFlow.MemberRegistered',
  'Growth.RegFlow.QuizCompleted',
] as const);

export const legacyParityEventNameSchema = z.enum(LEGACY_PARITY_EVENT_NAMES);
export type LegacyParityEventName = z.infer<typeof legacyParityEventNameSchema>;

// Each schema is strict so a per-person id, typed text or an attribution key
// can never ride along, even when production sent it.
const regFlowViewedPropertiesSchema = z
  .object({ entry_screen: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/) })
  .strict();

const noPropertiesSchema = z.object({}).strict();

const employerSearchedPropertiesSchema = z
  .object({
    search_type: z.enum(['employer', 'esi_carrier']),
    search_result: z.enum(['covered', 'not_covered', 'no_results']),
    company_id: z.number().int().positive().optional(),
    company_name: z.string().min(1).max(200).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.search_result === 'no_results' &&
      (value.company_id !== undefined || value.company_name !== undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A search with no results names no company',
      });
    }
  });

export const LEGACY_PARITY_PROPERTIES_SCHEMAS = {
  'Growth.RegFlow.Viewed': regFlowViewedPropertiesSchema,
  'Growth.RegFlow.EmployerSearched': employerSearchedPropertiesSchema,
  'Growth.RegFlow.MemberRegistered': noPropertiesSchema,
  'Growth.RegFlow.QuizCompleted': noPropertiesSchema,
} as const satisfies Record<LegacyParityEventName, z.ZodTypeAny>;

export type LegacyParityPropertiesInput<Name extends LegacyParityEventName> = z.input<
  (typeof LEGACY_PARITY_PROPERTIES_SCHEMAS)[Name]
>;
